/// <reference lib="dom" />
/**
 * Quality-tier detection (brief §5.3): cheap signals on first load, cached in
 * localStorage (try/catch — private windows throw), with a player override.
 * prefers-reduced-motion is surfaced separately; views reduce shake/particles
 * but never become unplayable.
 */

import type { QualityTier } from '@hnplay/shared';

const STORAGE_KEY = 'hnplay:tier';

export function detectTier(gpuRendererString = ''): QualityTier {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached === 'low' || cached === 'medium' || cached === 'high') return cached;
  } catch {
    /* storage unavailable — detect fresh */
  }

  const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 2 : 2;
  const memory = typeof navigator !== 'undefined' ? (navigator as { deviceMemory?: number }).deviceMemory ?? 4 : 4;
  const gpu = gpuRendererString.toLowerCase();
  const weakGpu = /(mali-4|mali-t|adreno [1-4]|powervr|swiftshader|llvmpipe|softpipe)/.test(gpu);

  let tier: QualityTier;
  if (weakGpu || cores <= 2 || memory <= 2) tier = 'low';
  else if (cores <= 4 || memory <= 4) tier = 'medium';
  else tier = 'high';

  try {
    localStorage.setItem(STORAGE_KEY, tier);
  } catch {
    /* fine */
  }
  return tier;
}

export function overrideTier(tier: QualityTier): void {
  try {
    localStorage.setItem(STORAGE_KEY, tier);
  } catch {
    /* fine */
  }
}

/** Demote one step after a sustained slow frame-time sample (called by the shell). */
export function demote(tier: QualityTier): QualityTier {
  return tier === 'high' ? 'medium' : 'low';
}

export function prefersReducedMotion(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}